import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import router from './router'
import store from './store'
import ElementPlus from 'element-plus'
import * as ElementPlusIconsVue from '@element-plus/icons-vue'
import 'element-plus/dist/index.css'
import axios from 'axios'
import { setupErrorHandlers } from './utils/errorHandler'
import { resizeObserverFix } from './plugins/resizeObserverFix'

const normalizeToken = (token) => {
    if (!token) return ''
    return String(token).replace(/^Bearer\s+/i, '').trim()
}

// 发布后若 CDN/浏览器仍缓存旧入口，会请求已不存在的 chunk，触发 ChunkLoadError / ERR_HTTP2_PROTOCOL_ERROR；整页刷新一次即可对齐新构建
const CHUNK_RELOAD_KEY = 'chunk_reload_v1'
function setupChunkLoadRecovery() {
    if (typeof window === 'undefined' || process.env.NODE_ENV === 'development') return
    window.addEventListener('load', () => {
        setTimeout(() => sessionStorage.removeItem(CHUNK_RELOAD_KEY), 8000)
    })
    window.addEventListener('unhandledrejection', (event) => {
        const r = event.reason
        const msg = r && (r.message || r.name || String(r))
        if (!msg || !/Loading chunk|ChunkLoadError|dynamically imported module|Importing a module script failed/i.test(String(msg))) {
            return
        }
        if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) return
        sessionStorage.setItem(CHUNK_RELOAD_KEY, '1')
        event.preventDefault()
        window.location.reload()
    })
}
setupChunkLoadRecovery()

// 跨域跳转 token 注入：优先从 #token=...（不进入请求行，避免 JWT 过长 414）；兼容 ?token=...
let injectedAdminToken = ''
try {
    const hash = window.location.hash || ''
    const hashTokenMatch = /^#token=(.+)$/.exec(hash)
    if (hashTokenMatch) {
        const token = normalizeToken(decodeURIComponent(hashTokenMatch[1]))
        if (token) {
            localStorage.setItem('admin_token', token)
            injectedAdminToken = token
            window.history.replaceState({}, document.title, window.location.pathname + window.location.search)
        }
    } else {
        const url = new URL(window.location.href)
        const qToken = url.searchParams.get('token')
        if (qToken) {
            const token = normalizeToken(qToken)
            localStorage.setItem('admin_token', token)
            injectedAdminToken = token
            url.searchParams.delete('token')
            const query = url.searchParams.toString()
            const nextUrl = url.pathname + (query ? `?${query}` : '') + url.hash
            window.history.replaceState({}, document.title, nextUrl)
        }
    }
} catch (e) {
    // ignore
}

// 重要：store 在 import 时已初始化，需把注入 token 回填到 store，避免首屏路由守卫误判未登录
if (injectedAdminToken) {
    store.commit('SET_ADMIN_TOKEN', injectedAdminToken)
}

// 配置axios
//const isDev = process.env.NODE_ENV === 'development';

// 如果是开发环境，用代理前缀，比如 /api
// 如果是生产环境，默认就是当前域
//const baseURL = isDev ? '/api' : `${window.location.protocol}//${window.location.hostname}`;

// const request = axios.create({
//     baseURL: baseURL,
//     timeout: 5000
// });


axios.interceptors.request.use(config => {
    const token = localStorage.getItem('token')
    if (token) {
        config.headers.Authorization = `Bearer ${token}`
    }
    return config
})

// 设置错误处理器
setupErrorHandlers()

const app = createApp(App)
const pinia = createPinia()

// 注册所有图标
for (const [key, component] of Object.entries(ElementPlusIconsVue)) {
    app.component(key, component)
}

app.use(pinia)
app.use(store)
app.use(router)
app.use(ElementPlus)
app.use(resizeObserverFix) // 应用 ResizeObserver 修复插件
app.mount('#app') 